import React from "react";
import { useLang } from "@/App";
import { legal, SOCIALS, EXTERNAL_LEGAL } from "@/lib/legal";
import { openCookieSettings } from "@/components/CookieBanner";

const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);

const IG = () => (
  <Icon d={<>
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="18" cy="6" r="0.5" fill="currentColor" />
  </>} />
);
const FB = () => (<Icon d={<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />} />);
const LI = () => (<Icon d={<>
  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-11h4v2" />
  <rect x="2" y="9" width="4" height="12" />
  <circle cx="4" cy="4" r="2" />
</>} />);
const YT = () => (<Icon d={<>
  <rect x="2" y="5" width="20" height="14" rx="3" />
  <path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" />
</>} />);

export default function LegalFooter() {
  const { lang, t } = useLang();
  const L = legal[lang];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative" style={{ background: "#f4ecd8", color: "#3a3628" }}>
      {/* Top CTA strip (existing dark footer block keeps "Book your first session now" — this is a sibling beneath) */}
      <div className="container-x" style={{ padding: "72px 32px 32px" }}>
        {/* GDPR banner card */}
        <div className="rounded-2xl p-6 md:p-8 mb-12" style={{ background: "#fbf8f1", border: "1px solid #e2d8bc" }} data-testid="legal-gdpr">
          <div className="grid md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-1 flex md:justify-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#c9a961", color: "#0e0e0e" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z"/><path d="M9 12l2 2 4-4"/></svg>
              </div>
            </div>
            <div className="md:col-span-11">
              <div className="serif" style={{ fontSize: 22, color: "#1a1a1a" }}>{L.gdpr.title}</div>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4a4738" }}>{L.gdpr.body}</p>
            </div>
          </div>
        </div>

        {/* Main grid: 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand + about */}
          <div>
            <div className="serif" style={{ fontSize: 28, color: "#1a1a1a" }}>NeuroTheos</div>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "#6b6753" }}>
              {lang === "ro"
                ? "Cabinet de psihologie · sesiuni online și în persoană · EN & RO."
                : "Psychology practice · online and in-person sessions · EN & RO."}
            </p>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "#8a8470" }}>{L.sections.follow}</div>
              <div className="flex items-center gap-3">
                {[
                  ["instagram", SOCIALS.instagram, <IG />],
                  ["facebook", SOCIALS.facebook, <FB />],
                  ["linkedin", SOCIALS.linkedin, <LI />],
                  ["youtube", SOCIALS.youtube, <YT />],
                ].filter(([, url]) => url && !url.includes("[")).map(([key, url, icon]) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key} data-testid={`social-${key}`} className="w-9 h-9 rounded-full flex items-center justify-center transition" style={{ background: "#fbf8f1", border: "1px solid #e2d8bc", color: "#3a3628" }}>
                    {icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ color: "#8a8470" }}>{L.sections.navigation}</div>
            <ul className="space-y-2 text-sm">
              {[["about", t.nav.about], ["services", t.nav.services], ["explore", t.cards.eyebrow], ["reviews", t.nav.reviews], ["book", t.nav.book], ["faq", t.nav.explore]].map(([id, label]) => (
                <li key={id}>
                  <button onClick={() => scrollTo(id)} className="hover:text-[#0e0e0e] transition" data-testid={`footer-nav-${id}`}>{label}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ color: "#8a8470" }}>{L.sections.legal}</div>
            <ul className="space-y-2 text-sm">
              <li><a href="/legal/privacy" className="hover:text-[#0e0e0e] transition" data-testid="footer-link-privacy">{L.links.privacy}</a></li>
              <li><a href="/legal/terms" className="hover:text-[#0e0e0e] transition" data-testid="footer-link-terms">{L.links.terms}</a></li>
              <li><a href="/legal/cookies" className="hover:text-[#0e0e0e] transition" data-testid="footer-link-cookies">{L.links.cookies}</a></li>
              <li><a href={EXTERNAL_LEGAL.anpc_complaints} target="_blank" rel="noopener noreferrer" className="hover:text-[#0e0e0e] transition inline-flex items-center gap-1" data-testid="footer-link-anpc">{L.links.anpc} ↗</a></li>
              <li><a href={EXTERNAL_LEGAL.eu_odr} target="_blank" rel="noopener noreferrer" className="hover:text-[#0e0e0e] transition inline-flex items-center gap-1" data-testid="footer-link-odr">{L.links.sol} ↗</a></li>
              <li><a href={EXTERNAL_LEGAL.dataprotection_ro} target="_blank" rel="noopener noreferrer" className="hover:text-[#0e0e0e] transition inline-flex items-center gap-1" data-testid="footer-link-gdpr">ANSPDCP ↗</a></li>
            </ul>
          </div>

          {/* Contact / Company */}
          <div>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ color: "#8a8470" }}>{L.company.heading}</div>
            <ul className="space-y-2 text-sm" style={{ color: "#4a4738" }} data-testid="footer-company">
              {L.company.lines.map((row, i) => (
                <li key={i} className="leading-relaxed">
                  <span className="block text-[11px] uppercase tracking-widest" style={{ color: "#8a8470" }}>{row.label}</span>
                  <span className={row.value.includes("[") ? "italic opacity-80" : ""}>{row.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 pt-8 border-t" style={{ borderColor: "#e2d8bc" }}>
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "#c9a961" }}>{L.disclaimer.title}</div>
          <p className="text-xs leading-relaxed" style={{ color: "#6b6753", maxWidth: 900 }} data-testid="legal-disclaimer">{L.disclaimer.body}</p>
        </div>

        {/* ANPC badges row */}
        <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="anpc-badges">
          <a href={EXTERNAL_LEGAL.anpc_complaints} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition" style={{ background: "#fbf8f1", border: "1px solid #e2d8bc", color: "#3a3628" }}>
            ANPC · SAL
          </a>
          <a href={EXTERNAL_LEGAL.eu_odr} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition" style={{ background: "#fbf8f1", border: "1px solid #e2d8bc", color: "#3a3628" }}>
            EU · ODR
          </a>
          <span className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest" style={{ background: "#fbf8f1", border: "1px solid #e2d8bc", color: "#3a3628" }}>
            GDPR · UE 2016/679
          </span>
          <span className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest" style={{ background: "#fbf8f1", border: "1px solid #e2d8bc", color: "#3a3628" }}>
            COPSI
          </span>
        </div>

        {/* Bottom strip */}
        <div className="mt-10 pt-6 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-3" style={{ borderColor: "#e2d8bc" }}>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#8a8470" }}>{L.rights}</div>
          <div className="flex items-center gap-4">
            <button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="text-[11px] uppercase tracking-widest hover:text-[#0e0e0e] transition" style={{ color: "#c9a961" }}>
              {L.cookieBanner.reopen}
            </button>
            <span className="text-[11px]" style={{ color: "#8a8470" }}>
              {lang === "ro" ? "Site realizat cu grijă · v1.0" : "Crafted with care · v1.0"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
